import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Disclaimer } from "@/components/Disclaimer";
import { LANDING_PAGES } from "@/lib/landingPages";
import { breadcrumbSchema, faqSchema, softwareApplicationSchema } from "@/lib/seo";

const RELATED = [
  { to: "/ai-legal-research", label: "AI Legal Research" },
  { to: "/ai-legal-drafting", label: "AI Legal Drafting" },
  { to: "/ai-case-brief", label: "AI Case Brief" },
  { to: "/document-translation", label: "Document Translation" },
  { to: "/judiciary-preparation", label: "Judiciary Preparation" },
  { to: "/best-legal-ai-for-advocates", label: "Best Legal AI for Advocates" },
  { to: "/most-accurate-legal-ai", label: "Most Accurate Legal AI" },
];

export default function LandingPage() {
  const { slug } = useParams();
  const data = LANDING_PAGES[slug];
  if (!data) return <Navigate to="/" replace />;

  const Icon = data.icon;
  const schema = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: data.eyebrow, path: `/${slug}` },
    ]),
    faqSchema(data.faqs),
    softwareApplicationSchema(),
  ];
  const related = RELATED.filter((r) => r.to !== `/${slug}`).slice(0, 6);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid={`landing-${slug}`}>
      <Seo title={data.title} description={data.description} schema={schema} />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.12em] text-inkfaint" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-gold">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-inkmuted">{data.eyebrow}</span>
      </nav>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-gold">
          <Icon className="w-4 h-4" /> {data.eyebrow}
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-ink mt-4 leading-tight">
          {data.h1}
        </h1>
        <p className="text-inkmuted mt-6 text-base sm:text-lg max-w-3xl leading-relaxed">{data.intro}</p>

        <div className="mt-8 flex flex-wrap gap-4">
          <Link to={data.cta.to} data-testid="landing-cta"
            className="rounded-full bg-btn text-btnink hover:bg-btnhover px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all flex items-center gap-2">
            {data.cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/pricing"
            className="rounded-full border border-gold text-gold hover:bg-gold/10 px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all">
            View Pricing
          </Link>
        </div>
      </motion.div>

      {/* Features */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl text-ink">Key capabilities</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {data.features.map((f, i) => (
            <div key={i} className="glass-card rounded-2xl p-5 flex gap-3" data-testid="landing-feature">
              <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-inkmuted leading-relaxed">{f}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl text-ink">Frequently asked questions</h2>
        <div className="mt-6 space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="glass-card rounded-2xl p-6" data-testid="landing-faq">
              <h3 className="text-ink font-medium text-base">{f.q}</h3>
              <p className="text-sm text-inkmuted mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Internal linking */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl text-ink">Explore more of JusticeVerse</h2>
        <div className="flex flex-wrap gap-3 mt-6">
          {related.map((r) => (
            <Link key={r.to} to={r.to}
              className="rounded-full border border-hairline text-inkmuted hover:text-ink hover:border-gold/40 px-5 py-2.5 text-sm transition-all">
              {r.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mt-16 glass-card rounded-3xl p-10 text-center">
        <h2 className="font-serif text-3xl text-ink">Start with JusticeVerse today</h2>
        <p className="text-inkmuted mt-3 max-w-xl mx-auto">India&apos;s premier AI legal platform — research, drafting, case briefs, translation and judiciary prep in one workspace.</p>
        <Link to={data.cta.to}
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-btn text-btnink hover:bg-btnhover px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all">
          {data.cta.label} <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <Disclaimer className="mt-12" />
    </div>
  );
}
